import type { ReactElement } from 'react';
import { useEffect, useState } from 'react';
import { Box, Collapse, Menu, MenuItem, Stack, Typography, alpha, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { ExpandMore } from '@mui/icons-material';
import { resolveServiceIcon } from '../../constants/serviceIcons';
import type { FlowTab, TabKey } from '../../constants/moduleFlows';
import type { MasterCatalogGroup } from '../../constants/masters';

const mastersListScrollSx = (theme: Theme) => ({
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${alpha(theme.palette.text.primary, 0.22)} transparent`,
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-thumb': {
    bgcolor: alpha(theme.palette.text.primary, 0.18),
    borderRadius: 6,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    bgcolor: alpha(theme.palette.text.primary, 0.28),
  },
});

const GROUPS: { id: string; label: string; keys: TabKey[] }[] = [
  { id: 'overview', label: 'Overview', keys: ['masters', 'summary'] },
  {
    id: 'operations',
    label: 'Operations',
    keys: ['bookings', 'manual-booking', 'bidding', 'tracking', 'order-history', 'scheduled-orders', 'quotations'],
  },
  { id: 'people', label: 'People', keys: ['users', 'partners', 'providers'] },
  {
    id: 'catalog',
    label: 'Catalog',
    keys: ['catalog', 'fleet', 'routes', 'packages', 'venues', 'slots', 'schedule'],
  },
  { id: 'coverage', label: 'Coverage', keys: ['zones', 'pricing', 'partner-payout'] },
  { id: 'insights', label: 'Insights', keys: ['reviews', 'earnings'] },
  { id: 'setup', label: 'Setup', keys: ['settings'] },
];

export function groupServiceTabs(tabs: FlowTab[]) {
  const used = new Set<string>();
  const sections = GROUPS
    .map((group) => ({
      ...group,
      items: group.keys.map((key) => tabs.find((t) => t.key === key)).filter(Boolean) as FlowTab[],
    }))
    .filter((group) => group.items.length > 0);
  sections.forEach((g) => g.items.forEach((t) => used.add(t.key)));
  const leftover = tabs.filter((t) => !used.has(t.key));
  if (leftover.length) {
    sections.push({ id: 'more', label: 'More', keys: leftover.map((t) => t.key), items: leftover });
  }
  return sections;
}

export type MasterNavSelect = (groupKey: string, typeKey: string) => void;

interface Props {
  tabs: FlowTab[];
  activeKey: string;
  onSelect: (key: string) => void;
  compact?: boolean;
  masterCatalog?: MasterCatalogGroup[];
  activeMasterType?: string;
  onSelectMaster?: MasterNavSelect;
}

function tabButtonSx(selected: boolean, ink: string, theme: Theme) {
  return {
    all: 'unset' as const,
    boxSizing: 'border-box' as const,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 1.1,
    width: '100%',
    px: 1.25,
    py: 0.85,
    borderRadius: 1.75,
    color: selected ? '#fff' : 'text.secondary',
    bgcolor: selected ? ink : 'transparent',
    fontWeight: selected ? 650 : 500,
    fontSize: '0.8125rem',
    letterSpacing: '-0.015em',
    transition: 'background-color 0.14s ease, color 0.14s ease',
    '&:hover': {
      bgcolor: selected ? ink : alpha(theme.palette.text.primary, 0.05),
      color: selected ? '#fff' : 'text.primary',
    },
    '& svg': {
      fontSize: 18,
      color: selected ? '#fff' : ink,
      opacity: selected ? 1 : 0.72,
      flexShrink: 0,
    },
  };
}

export default function ServiceWorkspaceNav({
  tabs,
  activeKey,
  onSelect,
  compact,
  masterCatalog = [],
  activeMasterType,
  onSelectMaster,
}: Props) {
  const theme = useTheme();
  const ink = theme.palette.primary.main;
  const sections = groupServiceTabs(tabs);
  const visibleMasterCatalog = masterCatalog
    .map((group) => ({
      ...group,
      types: group.types.filter((t) => t.key?.trim() && t.label?.trim()),
    }))
    .filter((group) => group.types.length > 0);
  const hasMastersMenu = visibleMasterCatalog.length > 0;
  const [mastersOpen, setMastersOpen] = useState(activeKey === 'masters');
  const [compactAnchor, setCompactAnchor] = useState<null | HTMLElement>(null);

  useEffect(() => {
    if (activeKey === 'masters') setMastersOpen(true);
  }, [activeKey]);

  const openMasters = () => {
    onSelect('masters');
    setMastersOpen(true);
  };

  const toggleMasters = () => {
    if (activeKey === 'masters') {
      setMastersOpen((open) => !open);
      return;
    }
    openMasters();
  };

  if (compact) {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            gap: 0.75,
            overflowX: 'auto',
            pb: 0.25,
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {tabs.map((tab) => {
            const selected = activeKey === tab.key;
            const isMasters = tab.key === 'masters' && hasMastersMenu;
            return (
              <Box
                key={tab.key}
                component="button"
                type="button"
                onClick={(e) => {
                  if (isMasters) {
                    setCompactAnchor(e.currentTarget);
                    onSelect('masters');
                    return;
                  }
                  onSelect(tab.key);
                }}
                sx={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  flexShrink: 0,
                  px: 1.35,
                  py: 0.85,
                  borderRadius: 99,
                  fontSize: '0.78rem',
                  fontWeight: selected ? 700 : 550,
                  letterSpacing: '-0.01em',
                  color: selected ? '#fff' : 'text.secondary',
                  bgcolor: selected ? ink : alpha(theme.palette.text.primary, 0.04),
                  border: `1px solid ${selected ? ink : theme.palette.divider}`,
                  '& svg': { fontSize: 16 },
                }}
              >
                {resolveServiceIcon(tab.icon) as ReactElement}
                {tab.label}
                {isMasters && <ExpandMore sx={{ fontSize: '16px !important', ml: -0.25 }} />}
              </Box>
            );
          })}
        </Box>
        <Menu
          anchorEl={compactAnchor}
          open={Boolean(compactAnchor)}
          onClose={() => setCompactAnchor(null)}
          slotProps={{ paper: { sx: { minWidth: 220, maxHeight: 420, ...mastersListScrollSx(theme) } } }}
        >
          {visibleMasterCatalog.flatMap((group) => {
            const header = visibleMasterCatalog.length > 1 && group.label?.trim() ? (
              <Typography
                key={`${group.key}-label`}
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 0.4,
                  fontSize: '0.62rem',
                  fontWeight: 750,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: 'text.disabled',
                }}
              >
                {group.label}
              </Typography>
            ) : null;
            const items = group.types.map((type) => (
              <MenuItem
                key={type.key}
                selected={activeMasterType === type.key}
                onClick={() => {
                  onSelectMaster?.(group.key, type.key);
                  setCompactAnchor(null);
                }}
              >
                {type.label}
              </MenuItem>
            ));
            return header ? [header, ...items] : items;
          })}
        </Menu>
      </>
    );
  }

  return (
    <Stack spacing={1.5}>
      {sections.map((section) => (
        <Box key={section.id}>
          <Typography
            sx={{
              px: 1.25,
              mb: 0.5,
              fontSize: '0.62rem',
              fontWeight: 750,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'text.disabled',
            }}
          >
            {section.label}
          </Typography>
          <Stack spacing={0.3}>
            {section.items.map((tab) => {
              const selected = activeKey === tab.key;
              const isMasters = tab.key === 'masters' && hasMastersMenu;

              if (isMasters) {
                return (
                  <Box key={tab.key}>
                    <Box
                      component="button"
                      type="button"
                      onClick={toggleMasters}
                      sx={tabButtonSx(selected, ink, theme)}
                    >
                      {resolveServiceIcon(tab.icon) as ReactElement}
                      <Box component="span" sx={{ flex: 1, whiteSpace: 'nowrap' }}>{tab.label}</Box>
                      <ExpandMore
                        sx={{
                          fontSize: '16px !important',
                          ml: 'auto',
                          opacity: '0.7 !important',
                          transform: mastersOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.15s',
                        }}
                      />
                    </Box>
                    <Collapse in={mastersOpen} timeout="auto" unmountOnExit>
                      <Stack
                        spacing={0.15}
                        sx={{
                          mt: 0.25,
                          ml: 1.25,
                          pl: 1,
                          borderLeft: 1,
                          borderColor: 'divider',
                          maxHeight: 340,
                          overflowY: 'auto',
                          pr: 0.5,
                          ...mastersListScrollSx(theme),
                        }}
                      >
                        {visibleMasterCatalog.map((group) => (
                          <Box key={group.key}>
                            {visibleMasterCatalog.length > 1 && group.label?.trim() && (
                              <Typography
                                sx={{
                                  px: 1,
                                  pt: 0.7,
                                  pb: 0.25,
                                  fontSize: '0.58rem',
                                  fontWeight: 750,
                                  letterSpacing: '0.08em',
                                  textTransform: 'uppercase',
                                  color: 'text.disabled',
                                }}
                              >
                                {group.label}
                              </Typography>
                            )}
                            {group.types.map((type) => {
                              const typeSelected = selected && activeMasterType === type.key;
                              return (
                                <Box
                                  key={type.key}
                                  component="button"
                                  type="button"
                                  onClick={() => onSelectMaster?.(group.key, type.key)}
                                  sx={{
                                    all: 'unset',
                                    boxSizing: 'border-box',
                                    cursor: 'pointer',
                                    display: 'block',
                                    width: '100%',
                                    px: 1,
                                    py: 0.65,
                                    borderRadius: 1.25,
                                    color: typeSelected ? ink : 'text.secondary',
                                    bgcolor: typeSelected ? alpha(ink, 0.1) : 'transparent',
                                    fontWeight: typeSelected ? 650 : 500,
                                    fontSize: '0.78rem',
                                    letterSpacing: '-0.01em',
                                    '&:hover': {
                                      bgcolor: typeSelected ? alpha(ink, 0.12) : alpha(theme.palette.text.primary, 0.04),
                                      color: typeSelected ? ink : 'text.primary',
                                    },
                                  }}
                                >
                                  {type.label}
                                </Box>
                              );
                            })}
                          </Box>
                        ))}
                      </Stack>
                    </Collapse>
                  </Box>
                );
              }

              return (
                <Box
                  key={tab.key}
                  component="button"
                  type="button"
                  onClick={() => onSelect(tab.key)}
                  sx={tabButtonSx(selected, ink, theme)}
                >
                  {resolveServiceIcon(tab.icon) as ReactElement}
                  <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{tab.label}</Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}
